import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type CardPatch } from './api'
import { COLUMNS, type Assignee, type Card, type Status } from './types'

export type Columns = Record<Status, Card[]>
type BoardState = { columns: Columns; done: Card[] }

const emptyColumns = (): Columns =>
  Object.fromEntries(COLUMNS.map((c) => [c.id, [] as Card[]])) as Columns

function groupByColumn(cards: Card[]): Columns {
  const grouped = emptyColumns()
  for (const card of cards) grouped[card.status]?.push(card)
  for (const status of Object.keys(grouped) as Status[]) {
    grouped[status].sort((a, b) => a.position - b.position)
  }
  return grouped
}

const mapCard = (state: BoardState, id: string, fn: (card: Card) => Card): BoardState => ({
  columns: Object.fromEntries(
    (Object.keys(state.columns) as Status[]).map((s) => [
      s,
      state.columns[s].map((c) => (c.id === id ? fn(c) : c)),
    ]),
  ) as Columns,
  done: state.done.map((c) => (c.id === id ? fn(c) : c)),
})

const withoutCard = (state: BoardState, id: string): BoardState => ({
  columns: Object.fromEntries(
    (Object.keys(state.columns) as Status[]).map((s) => [
      s,
      state.columns[s].filter((c) => c.id !== id),
    ]),
  ) as Columns,
  done: state.done.filter((c) => c.id !== id),
})

export function useBoard() {
  const [state, setState] = useState<BoardState>({ columns: emptyColumns(), done: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Mirrors `state` so an event handler can grab the pre-write board synchronously.
  const latest = useRef(state)
  useEffect(() => {
    latest.current = state
  }, [state])

  const reload = useCallback(async () => {
    try {
      const { active, done } = await api.listCards()
      setState({ columns: groupByColumn(active), done })
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the board')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  /**
   * Apply an optimistic board change, then persist it. If the write fails the
   * board snaps back to exactly how it looked before and we say why.
   */
  const optimistic = useCallback(
    async (
      apply: (state: BoardState) => BoardState,
      persist: () => Promise<void>,
      fallbackMessage: string,
    ) => {
      const before = latest.current
      const next = apply(before)
      latest.current = next
      setState(next)
      try {
        await persist()
        setError(null)
      } catch (err) {
        latest.current = before
        setState(before)
        setError(err instanceof Error ? err.message : fallbackMessage)
      }
    },
    [],
  )

  const addCard = useCallback(
    async (input: { title: string; assignee: Assignee; status: Status }) => {
      const pendingId = `pending-${crypto.randomUUID()}`
      const optimisticCard: Card = {
        ...input,
        id: pendingId,
        notes: null,
        dueDate: null,
        waitingOn: null,
        position: Number.MAX_SAFE_INTEGER,
        doneAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      await optimistic(
        (s) => ({ ...s, columns: { ...s.columns, [input.status]: [...s.columns[input.status], optimisticCard] } }),
        async () => {
          const saved = await api.createCard(input)
          setState((s) => {
            const next = mapCard(s, pendingId, () => saved)
            latest.current = next
            return next
          })
        },
        'Could not add that card',
      )
    },
    [optimistic],
  )

  const patchCard = useCallback(
    async (id: string, patch: CardPatch) => {
      await optimistic(
        (s) => mapCard(s, id, (card) => ({ ...card, ...patch })),
        async () => {
          const saved = await api.updateCard(id, patch)
          setState((s) => {
            const next = mapCard(s, id, () => saved)
            latest.current = next
            return next
          })
        },
        'Could not save that change',
      )
    },
    [optimistic],
  )

  /** Board has already reordered locally during the drag; just persist the result. */
  const persistMove = useCallback(
    async (id: string, columns: Columns, status: Status, index: number) => {
      await optimistic(
        (s) => ({ ...s, columns }),
        async () => {
          await api.updateCard(id, { status, index })
        },
        'Could not move that card',
      )
    },
    [optimistic],
  )

  const markDone = useCallback(
    async (card: Card) => {
      await optimistic(
        (s) => {
          const stripped = withoutCard(s, card.id)
          return { ...stripped, done: [{ ...card, doneAt: new Date().toISOString() }, ...stripped.done] }
        },
        async () => {
          const saved = await api.updateCard(card.id, { done: true })
          setState((s) => {
            const next = mapCard(s, card.id, () => saved)
            latest.current = next
            return next
          })
        },
        'Could not tick that card off',
      )
    },
    [optimistic],
  )

  const restore = useCallback(
    async (card: Card) => {
      await optimistic(
        (s) => {
          const stripped = withoutCard(s, card.id)
          return {
            ...stripped,
            columns: {
              ...stripped.columns,
              [card.status]: [...stripped.columns[card.status], { ...card, doneAt: null }],
            },
          }
        },
        async () => {
          const saved = await api.updateCard(card.id, { done: false })
          setState((s) => {
            const next = mapCard(s, card.id, () => saved)
            latest.current = next
            return next
          })
        },
        'Could not bring that card back',
      )
    },
    [optimistic],
  )

  const removeCard = useCallback(
    async (card: Card) => {
      await optimistic(
        (s) => withoutCard(s, card.id),
        async () => {
          await api.deleteCard(card.id)
        },
        'Could not delete that card',
      )
    },
    [optimistic],
  )

  /** Live reorder while a card is mid-drag — not persisted until the drop lands. */
  const previewColumns = useCallback((columns: Columns) => {
    setState((s) => ({ ...s, columns }))
  }, [])

  const allCards = useMemo(
    () => [...COLUMNS.flatMap((c) => state.columns[c.id]), ...state.done],
    [state],
  )

  return {
    columns: state.columns,
    done: state.done,
    allCards,
    loading,
    error,
    dismissError: () => setError(null),
    reload,
    addCard,
    patchCard,
    persistMove,
    previewColumns,
    markDone,
    restore,
    removeCard,
  }
}
